import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { Phone, PhoneCall, Clock, PlayCircle, Download, Filter, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ExpressIVRCallCenter({ user, onLogout }) {
  const [callHistory, setCallHistory] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialModal, setShowDialModal] = useState(false);
  const [filterPhone, setFilterPhone] = useState('');
  const [days, setDays] = useState(7);
  
  // Click-to-Dial form data
  const [dialData, setDialData] = useState({
    agent_phone: '',
    customer_phone: '',
    desk_phone: 'desk-001',
    call_group: 'sales'
  });

  // Stats
  const [stats, setStats] = useState({
    totalCalls: 0,
    answeredCalls: 0,
    missedCalls: 0,
    avgDuration: 0
  });

  useEffect(() => {
    fetchCallHistory();
  }, [days]);

  useEffect(() => {
    filterCalls();
  }, [filterPhone, callHistory]);

  const fetchCallHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/express-ivr/call-history`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days, limit: 100 }
      });
      
      const calls = response.data || [];
      setCallHistory(calls);
      calculateStats(calls);
    } catch (error) {
      console.error('Error fetching call history:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (calls) => {
    const totalCalls = calls.length;
    const answeredCalls = calls.filter(c => c.status === 'ANSWERED').length;
    const missedCalls = calls.filter(c => c.status === 'CANCEL' || c.status === 'NO_ANSWER').length;
    const totalDuration = calls.reduce((sum, c) => sum + (c.call_duration || 0), 0);
    const avgDuration = totalCalls > 0 ? Math.round(totalDuration / totalCalls) : 0;

    setStats({
      totalCalls,
      answeredCalls,
      missedCalls,
      avgDuration
    });
  };

  const filterCalls = () => {
    if (!filterPhone) {
      setFilteredCalls(callHistory);
      return;
    }
    
    const filtered = callHistory.filter(call => 
      call.source_number.includes(filterPhone) || 
      call.destination_number.includes(filterPhone)
    );
    setFilteredCalls(filtered);
  };

  const handleClickToDial = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/express-ivr/click-to-dial`,
        dialData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('📞 Call initiated successfully! Agent will receive call first, then customer will be connected.');
      setShowDialModal(false);
      setDialData({
        agent_phone: '',
        customer_phone: '',
        desk_phone: 'desk-001',
        call_group: 'sales'
      });
      
      // Refresh call history after a delay
      setTimeout(() => fetchCallHistory(), 3000);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'ANSWERED': { color: '#4caf50', text: 'Answered' },
      'CANCEL': { color: '#f44336', text: 'Cancelled' },
      'NO_ANSWER': { color: '#ff9800', text: 'No Answer' },
      'BUSY': { color: '#9c27b0', text: 'Busy' },
    };
    
    const badge = badges[status] || { color: '#757575', text: status };
    
    return (
      <span 
        className="px-3 py-1 rounded-full text-sm font-medium text-white"
        style={{ backgroundColor: badge.color }}
      >
        {badge.text}
      </span>
    );
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { 
      dateStyle: 'short', 
      timeStyle: 'short' 
    });
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#2b3380' }}>Express IVR Call Center</h1>
            <p className="text-gray-600 mt-2">Manage calls with Click-to-Dial and real-time tracking</p>
          </div>
          <button
            onClick={() => setShowDialModal(true)}
            className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
            style={{ backgroundColor: '#2b3380' }}
          >
            <PhoneCall className="w-5 h-5" />
            Click-to-Dial
          </button>
        </div>

        {/* Stats Dashboard */}
        <MiniDashboard
          title="Call Center Overview"
          icon="📞"
          bgColor="#1976d2"
          stats={[
            { label: 'Total Calls', value: stats.totalCalls, subtitle: `Last ${days} days`, color: '#2b3380' },
            { label: 'Answered', value: stats.answeredCalls, subtitle: 'Successful', color: '#4caf50' },
            { label: 'Missed', value: stats.missedCalls, subtitle: 'No answer/Cancel', color: '#f44336' },
            { label: 'Avg Duration', value: `${Math.floor(stats.avgDuration / 60)}m`, subtitle: 'Per call', color: '#ff9800' }
          ]}
        />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Filter by Phone Number</label>
              <input
                type="text"
                value={filterPhone}
                onChange={(e) => setFilterPhone(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter phone number..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Date Range</label>
              <select
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value={1}>Today</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchCallHistory}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Call History Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ backgroundColor: '#2b3380' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-white">Time</th>
                  <th className="px-6 py-3 text-left text-white">Caller</th>
                  <th className="px-6 py-3 text-left text-white">Recipient</th>
                  <th className="px-6 py-3 text-left text-white">Agent</th>
                  <th className="px-6 py-3 text-left text-white">Status</th>
                  <th className="px-6 py-3 text-left text-white">Duration</th>
                  <th className="px-6 py-3 text-left text-white">Recording</th>
                </tr>
              </thead>
              <tbody>
                {loading && !callHistory.length ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <RefreshCw className="w-8 h-8 animate-spin mb-2" style={{ color: '#2b3380' }} />
                        <p>Loading call history...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredCalls.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <Phone className="w-12 h-12 mb-2 text-gray-400" />
                        <p>No call records found</p>
                        {filterPhone && <p className="text-sm">Try adjusting your filters</p>}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredCalls.map((call) => (
                    <tr key={call.id} className="border-b hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{formatDateTime(call.start_time || call.created_at)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium">{call.source_number}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium">{call.destination_number}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">{call.receiver_name || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(call.status)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium">{formatDuration(call.call_duration)}</span>
                      </td>
                      <td className="px-6 py-4">
                        {call.call_recording_url ? (
                          <div className="flex gap-2">
                            <a
                              href={call.call_recording_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                            >
                              <PlayCircle className="w-4 h-4" />
                              Play
                            </a>
                            <a
                              href={call.call_recording_url}
                              download
                              className="text-green-600 hover:underline flex items-center gap-1 text-sm"
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </a>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Click-to-Dial Modal */}
        {showDialModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#2b3380' }}>
                Initiate Call
              </h2>
              
              <form onSubmit={handleClickToDial}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Agent Phone Number *</label>
                    <input
                      type="tel"
                      value={dialData.agent_phone}
                      onChange={(e) => setDialData({ ...dialData, agent_phone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="+919599900999"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Agent will receive call first</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Customer Phone Number *</label>
                    <input
                      type="tel"
                      value={dialData.customer_phone}
                      onChange={(e) => setDialData({ ...dialData, customer_phone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="+911234567890"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Customer will be connected after agent picks up</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Desk Phone ID *</label>
                    <input
                      type="text"
                      value={dialData.desk_phone}
                      onChange={(e) => setDialData({ ...dialData, desk_phone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="desk-001"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Call Group (Optional)</label>
                    <select
                      value={dialData.call_group}
                      onChange={(e) => setDialData({ ...dialData, call_group: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="sales">Sales</option>
                      <option value="support">Support</option>
                      <option value="billing">Billing</option>
                      <option value="general">General</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-2 mt-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 text-white rounded-lg font-medium flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#2b3380' }}
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Calling...
                      </>
                    ) : (
                      <>
                        <PhoneCall className="w-4 h-4" />
                        Initiate Call
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDialModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ExpressIVRCallCenter;